import React, { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { connect, useSelector } from "react-redux";
import { updateVideoGroup } from "../store/action";
import { GroupError, GroupInput, VideoGroupProps } from "../store/actionType";
import { GroupForm } from "modules/video/components/CreateFolderForm";
import { ApiError } from "components";

interface EditGroupProps {
  closeModal: React.Dispatch<React.SetStateAction<boolean>>;
  groupData: VideoGroupProps;
}

type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = DispatchProps & EditGroupProps;

const EditGroup: React.FC<Props> = ({
  closeModal,
  groupData,
  onUpdateVideoGroup,
}) => {
  const [formData, setFormData] = useState<GroupInput>({
    name: groupData.name,
    description: groupData.description || "",
    group_order: groupData.group_order || 0,
    image: groupData.image || "",
    icon: groupData.icon || "",
    imageFile: undefined,
    button_name:groupData.button_name,
    eligibility:groupData.eligibility
  });
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [error, setError] = useState<GroupError | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [apiError, setApiError] = useState(null);
  const groupsState = useSelector(
    (state: any) => state.videoGroups.videoGroupsState
  );

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "number" ? Number(value) : value,
    }));
    setError((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    if (formData.name.trim() === "") {
      setError((prevErrors) => ({
        ...prevErrors,
        name: "Group name is required",
      }));
    }
    if (formData.description.trim() === "") {
      setError((prevErrors) => ({
        ...prevErrors,
        description: "Group description is required",
      }));
    }
     if (formData.group_order === 0) {
      setError((prevErrors) => ({
        ...prevErrors,
        group_order: "Group group order is required",
      }));
    }
    let uploadedFilename: string | undefined = undefined;
    if (imageFile) {
      const uploadUrl = `${process.env.REACT_APP_IMAGE_UPLOAD_BASE_URL}`;
      const form = new FormData();
      form.append("file", imageFile);
      const response = await fetch(uploadUrl ?? "", {
        method: "POST",
        body: form,
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error("Upload failed:", errorText);
        throw new Error("Failed to upload image");
      }

      const result = await response.json();
      uploadedFilename = result?.imageURL;

      if (!uploadedFilename) {
        console.error("Image upload response invalid", result);
        throw new Error("Image upload response invalid");
      }
    }
    if (formData.name.trim() !== "" && formData.description.trim() !== "") {
      const input: GroupInput = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        group_order: formData.group_order,
        button_name:formData.button_name,
        eligibility:formData.eligibility
      };

      if (formData.icon) {
        input.icon = formData.icon.trim();
      }

      if (uploadedFilename) {
        input.image = uploadedFilename;
      }
      setLoading(true);
      onUpdateVideoGroup(
        input,
        groupData.id.toString(),
        groupData.folder_id.toString(),
      );
      setSubmitted(true);
      setLoading(false);
    }
  };
  useEffect(() => {
    if (!groupsState.isLoading && submitted) {
      if (groupsState.error) {
        setApiError(groupsState.error); // show error
      } else {
        setApiError(null);
        closeModal(false);
      }
    }
  }, [groupsState.isLoading, groupsState.error, submitted, closeModal]);
  return (
    <>
      <GroupForm
        header="Edit Group"
        formData={formData}
        error={error}
        handleChange={handleChange}
        handleSubmit={handleSave}
        loading={loading}
        handleImageFileChange={setImageFile}
      />
      {apiError && <ApiError>{apiError}</ApiError>}
    </>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  onUpdateVideoGroup: (
    input: GroupInput,
    group_id: string,
    folder_id: string
  ) => dispatch(updateVideoGroup(input, group_id, folder_id)),
});

export default connect(null, mapDispatchToProps)(EditGroup);
